"use client";

import {
  ArrowLeft,
  BookOpen,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Cloud,
  Clock3,
  Disc3,
  FileAudio,
  FileText,
  Gauge,
  Library,
  Layers3,
  LoaderCircle,
  LogOut,
  Menu,
  Pause,
  Play,
  Plus,
  Repeat2,
  RotateCcw,
  Sparkles,
  Trash2,
  UploadCloud,
  UserRound,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import { ChangeEvent, Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { BookLanguage } from "@japa-reader/contracts";
import type { AuthUser } from "../auth";
import { BOOK_LANGUAGES, bookLanguageMeta, normalizeBookLanguage, type LibraryLanguageFilter, validLanguageFilter } from "../lib/book-language";
import { extractBook, type ExtractedBook } from "../lib/epub";
import { computeExplanationLayout } from "../lib/reader-motion";
import { buildSentences, type ReaderSentence } from "../lib/reader-sentences";
import { Flashcards, type FlashcardSeed } from "./Flashcards";
import { VocabularyFlashcardDialog, type VocabularyFlashcardContext } from "./VocabularyFlashcardDialog";
import {
  alignStoredChapter,
  chapterProgress,
  deleteStoredBook,
  deleteStoredChapter,
  explainSentence,
  fingerprintChapter,
  getStoredChapter,
  listLibraryBooks,
  makeId,
  saveBookAndChapter,
  saveBookWithChapters,
  saveChapter,
  saveChapterProgress,
  type AlignedCharacter,
  type LibraryBook,
  type StoredBook,
  type StoredChapter,
} from "../lib/library";

type ViewMode = "library" | "book" | "reader" | "flashcards";

type SentenceExplanation = {
  translation: string;
  reading: string;
  grammar: string;
  vocabulary: Array<{ term: string; reading: string; meaning: string }>;
};

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];
const SPEECH_TO_TEXT_RATE = 0.22;
type JapaReaderProps = {
  user: AuthUser;
  onSignOut: () => void;
};

export function JapaReader({ user, onSignOut }: JapaReaderProps) {
  const [libraryBooks, setLibraryBooks] = useState<LibraryBook[]>([]);
  const [libraryReady, setLibraryReady] = useState(false);
  const [libraryError, setLibraryError] = useState("");
  const [libraryLanguage, setLibraryLanguage] = useState<LibraryLanguageFilter>(() => {
    if (typeof window === "undefined") return "all";
    const saved = window.localStorage.getItem("japa-reader-library-language");
    return validLanguageFilter(saved) ? saved : "all";
  });
  const [view, setView] = useState<ViewMode>("library");
  const [flashcardSeed, setFlashcardSeed] = useState<FlashcardSeed | null>(null);
  const [vocabularyFlashcardContext, setVocabularyFlashcardContext] = useState<VocabularyFlashcardContext | null>(null);
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null);
  const [activeStoredChapterId, setActiveStoredChapterId] = useState<string | null>(null);
  const [bookDialogOpen, setBookDialogOpen] = useState(false);
  const [newBookFile, setNewBookFile] = useState<File | null>(null);
  const [newBookData, setNewBookData] = useState<ExtractedBook | null>(null);
  const [newBookTitle, setNewBookTitle] = useState("");
  const [newBookAuthor, setNewBookAuthor] = useState("");
  const [newBookLanguage, setNewBookLanguage] = useState<BookLanguage>("ja");
  const [isBookParsing, setIsBookParsing] = useState(false);
  const [isBookSaving, setIsBookSaving] = useState(false);

  const [alignment, setAlignment] = useState<AlignedCharacter[]>([]);
  const [bookTitle, setBookTitle] = useState("");
  const [chapterTitle, setChapterTitle] = useState("");
  const [setupOpen, setSetupOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editingChapterId, setEditingChapterId] = useState<string | null>(null);
  const [bookFile, setBookFile] = useState<File | null>(null);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [sourceText, setSourceText] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [formError, setFormError] = useState("");
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [pendingAudioDuration, setPendingAudioDuration] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [loopSentenceIndex, setLoopSentenceIndex] = useState<number | null>(null);
  const [explainedSentenceIndex, setExplainedSentenceIndex] = useState<number | null>(null);
  const [sentenceExplanations, setSentenceExplanations] = useState<Record<number, SentenceExplanation>>({});
  const [explanationLoadingIndex, setExplanationLoadingIndex] = useState<number | null>(null);
  const [explanationError, setExplanationError] = useState("");

  const audioRef = useRef<HTMLAudioElement>(null);
  const objectUrlRef = useRef<string | null>(null);
  const activeChapterRef = useRef<StoredChapter | null>(null);
  const readerTopbarRef = useRef<HTMLElement>(null);
  const playerBarRef = useRef<HTMLElement>(null);
  const sentenceViewportRef = useRef<HTMLDivElement>(null);
  const sentenceRefs = useRef<Array<HTMLDivElement | null>>([]);
  const lastFrameRef = useRef(0);
  const lastSaveRef = useRef(0);
  const pendingRestoreRef = useRef(0);
  const hasCenteredSentenceRef = useRef(false);
  const scrollAnimationRef = useRef<number | null>(null);
  const sentenceAnimationRef = useRef<Animation | null>(null);
  const explanationViewportTopRef = useRef<number | null>(null);
  const explanationRevealFrameRef = useRef<number | null>(null);
  const pendingExplanationIndexRef = useRef<number | null>(null);
  const explanationRequestRef = useRef(0);

  const selectedBook = useMemo(
    () => libraryBooks.find((book) => book.id === selectedBookId) ?? null,
    [libraryBooks, selectedBookId],
  );
  const selectedBookLanguage = normalizeBookLanguage(selectedBook?.language);
  const visibleLibraryBooks = useMemo(
    () => libraryLanguage === "all" ? libraryBooks : libraryBooks.filter((book) => normalizeBookLanguage(book.language) === libraryLanguage),
    [libraryBooks, libraryLanguage],
  );
  const sentences = useMemo(() => buildSentences(alignment, selectedBookLanguage), [alignment, selectedBookLanguage]);
  const alignmentDuration = alignment.at(-1)?.end ?? 0;
  const duration = Math.max(audioDuration, alignmentDuration);
  const activeCharacterIndex = useMemo(
    () => findCharacterAtTime(alignment, currentTime),
    [alignment, currentTime],
  );
  const activeSentenceIndex = useMemo(
    () => findSentenceForCharacter(sentences, activeCharacterIndex),
    [sentences, activeCharacterIndex],
  );
  const activeChapterIndex = selectedBook?.chapters.findIndex((chapter) => chapter.id === activeStoredChapterId) ?? -1;

  const refreshLibrary = useCallback(async () => {
    try {
      const books = await listLibraryBooks();
      setLibraryBooks(books);
      setLibraryError("");
      setSelectedBookId((current) => current && books.some((book) => book.id === current) ? current : null);
    } catch (error) {
      setLibraryError(error instanceof Error ? error.message : "Não foi possível abrir sua biblioteca.");
    } finally {
      setLibraryReady(true);
    }
  }, []);

  useEffect(() => {
    const frame = requestAnimationFrame(() => void refreshLibrary());
    return () => cancelAnimationFrame(frame);
  }, [refreshLibrary]);

  useEffect(() => {
    window.localStorage.setItem("japa-reader-library-language", libraryLanguage);
  }, [libraryLanguage]);

  const savePosition = useCallback((time: number) => {
    const chapter = activeChapterRef.current;
    if (!chapter) return;
    const updated: StoredChapter = {
      ...chapter,
      position: Math.max(0, time),
      playbackRate,
      updatedAt: Date.now(),
    };
    activeChapterRef.current = updated;
    void saveChapterProgress(updated);
  }, [playbackRate]);

  const seek = useCallback((nextTime: number) => {
    const safeTime = Math.min(Math.max(0, nextTime), duration || 0);
    if (audioRef.current && audioUrl) audioRef.current.currentTime = safeTime;
    setCurrentTime(safeTime);
    if (loopSentenceIndex !== null) {
      const nextCharacterIndex = findCharacterAtTime(alignment, safeTime);
      const nextSentenceIndex = findSentenceForCharacter(sentences, nextCharacterIndex);
      setLoopSentenceIndex(nextSentenceIndex >= 0 ? nextSentenceIndex : null);
    }
    savePosition(safeTime);
  }, [alignment, audioUrl, duration, loopSentenceIndex, savePosition, sentences]);

  const togglePlay = useCallback(async () => {
    if (currentTime >= duration - 0.05) seek(0);
    if (!audioUrl || !audioRef.current) return;

    if (audioRef.current.paused) {
      try {
        await audioRef.current.play();
      } catch {
        setFormError("O navegador não conseguiu iniciar este áudio.");
      }
    } else {
      audioRef.current.pause();
    }
  }, [audioUrl, currentTime, duration, seek]);

  const playFrom = useCallback(async (nextTime: number) => {
    seek(nextTime);
    const audio = audioRef.current;
    if (!audioUrl || !audio) return;
    try {
      await audio.play();
    } catch {
      setFormError("O navegador não conseguiu iniciar este áudio.");
    }
  }, [audioUrl, seek]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
      audioRef.current.muted = isMuted;
    }
  }, [playbackRate, isMuted, audioUrl]);

  useEffect(() => {
    if (!isPlaying) {
      lastFrameRef.current = 0;
      return;
    }

    let frameId = 0;
    const frame = (now: number) => {
      const audio = audioRef.current;
      const loopedSentence = loopSentenceIndex === null ? null : sentences[loopSentenceIndex];
      if (audio && loopedSentence && audio.currentTime >= loopedSentence.end) {
        audio.currentTime = loopedSentence.start;
        setCurrentTime(loopedSentence.start);
      } else if (audio) {
        setCurrentTime(audio.currentTime);
      }
      if (now - lastSaveRef.current > 2000 && audioRef.current) {
        savePosition(audioRef.current.currentTime);
        lastSaveRef.current = now;
      }
      frameId = requestAnimationFrame(frame);
    };

    frameId = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(frameId);
  }, [isPlaying, loopSentenceIndex, savePosition, sentences]);

  const centerSentence = useCallback((index: number, animate: boolean) => {
    const viewport = sentenceViewportRef.current;
    const sentence = sentenceRefs.current[index];
    if (!viewport || !sentence) return;

    if (scrollAnimationRef.current !== null) {
      cancelAnimationFrame(scrollAnimationRef.current);
      scrollAnimationRef.current = null;
    }

    const viewportRect = viewport.getBoundingClientRect();
    const sentenceRect = sentence.getBoundingClientRect();
    const topbarBottom = readerTopbarRef.current?.getBoundingClientRect().bottom ?? 0;
    const playerTop = playerBarRef.current?.getBoundingClientRect().top ?? window.innerHeight;
    const visibleTop = Math.max(0, viewportRect.top, topbarBottom);
    const visibleBottom = Math.min(window.innerHeight, viewportRect.bottom, playerTop);
    const visibleCenter = visibleBottom > visibleTop
      ? visibleTop + (visibleBottom - visibleTop) / 2
      : viewportRect.top + viewport.clientHeight / 2;
    const sentenceCenter = sentenceRect.top + sentenceRect.height / 2;
    const targetTop = Math.min(
      Math.max(0, viewport.scrollTop + sentenceCenter - visibleCenter),
      Math.max(0, viewport.scrollHeight - viewport.clientHeight),
    );
    const startTop = viewport.scrollTop;
    const distance = targetTop - startTop;

    if (!animate || Math.abs(distance) < 1) {
      viewport.scrollTop = targetTop;
      return;
    }

    const startedAt = performance.now();
    const durationMs = 760;
    const step = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / durationMs);
      const eased = 1 - Math.pow(1 - progress, 4);
      viewport.scrollTop = startTop + distance * eased;
      if (progress < 1) scrollAnimationRef.current = requestAnimationFrame(step);
      else {
        viewport.scrollTop = targetTop;
        scrollAnimationRef.current = null;
      }
    };

    scrollAnimationRef.current = requestAnimationFrame(step);
  }, []);

  const moveSentenceToExplanationTop = useCallback((index: number) => {
    const viewport = sentenceViewportRef.current;
    const sentence = sentenceRefs.current[index];
    if (!viewport || !sentence) return;

    if (scrollAnimationRef.current !== null) cancelAnimationFrame(scrollAnimationRef.current);
    sentenceAnimationRef.current?.cancel();
    sentenceAnimationRef.current = null;

    const viewportRect = viewport.getBoundingClientRect();
    const sentenceRect = sentence.getBoundingClientRect();
    const topbarBottom = readerTopbarRef.current?.getBoundingClientRect().bottom ?? 0;
    const playerTop = playerBarRef.current?.getBoundingClientRect().top ?? window.innerHeight;
    const layoutInput = {
      viewportTop: viewportRect.top,
      viewportBottom: viewportRect.bottom,
      viewportScrollTop: viewport.scrollTop,
      viewportScrollHeight: viewport.scrollHeight,
      viewportClientHeight: viewport.clientHeight,
      topbarBottom,
      playerTop,
      windowHeight: window.innerHeight,
      sentenceTop: sentenceRect.top,
      sentenceHeight: sentenceRect.height,
    };
    const initialLayout = computeExplanationLayout(layoutInput);
    const layout = computeExplanationLayout({
      ...layoutInput,
      viewportScrollHeight: viewport.scrollHeight + initialLayout.panelHeight,
    });
    viewport.style.setProperty("--explanation-panel-height", `${layout.panelHeight}px`);
    explanationViewportTopRef.current = layout.targetScrollTop;

    if (Math.abs(layout.translateY) < 1) {
      viewport.scrollTop = layout.targetScrollTop;
      return;
    }

    const animation = sentence.animate(
      [
        { transform: "translate3d(0, 0, 0)" },
        { transform: `translate3d(0, ${layout.translateY}px, 0)` },
      ],
      {
        duration: 720,
        easing: "cubic-bezier(0.22, 1, 0.36, 1)",
        fill: "forwards",
      },
    );
    sentenceAnimationRef.current = animation;
    animation.onfinish = () => {
      viewport.scrollTop = layout.targetScrollTop;
      animation.oncancel = null;
      animation.cancel();
      if (sentenceAnimationRef.current === animation) sentenceAnimationRef.current = null;
    };
    animation.oncancel = () => {
      if (sentenceAnimationRef.current === animation) sentenceAnimationRef.current = null;
    };
  }, []);

  const closeSentenceExplanation = useCallback(() => {
    if (explanationRevealFrameRef.current !== null) cancelAnimationFrame(explanationRevealFrameRef.current);
    explanationRevealFrameRef.current = null;
    pendingExplanationIndexRef.current = null;
    sentenceAnimationRef.current?.cancel();
    sentenceAnimationRef.current = null;
    explanationViewportTopRef.current = null;
    setExplainedSentenceIndex(null);
  }, []);

  function toggleSentenceExplanation(index: number) {
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      audio.pause();
      setIsPlaying(false);
      savePosition(audio.currentTime);
    }
    if (explainedSentenceIndex === index || pendingExplanationIndexRef.current === index) {
      closeSentenceExplanation();
      return;
    }

    moveSentenceToExplanationTop(index);
    if (explanationRevealFrameRef.current !== null) cancelAnimationFrame(explanationRevealFrameRef.current);
    pendingExplanationIndexRef.current = index;
    explanationRevealFrameRef.current = requestAnimationFrame(() => {
      explanationRevealFrameRef.current = requestAnimationFrame(() => {
        explanationRevealFrameRef.current = null;
        pendingExplanationIndexRef.current = null;
        setExplainedSentenceIndex(index);
      });
    });
    setExplanationError("");
    if (!sentenceExplanations[index] && explanationLoadingIndex !== index) {
      void loadSentenceExplanation(index);
    }
  }

  async function loadSentenceExplanation(index: number) {
    const sentence = sentences[index];
    if (!sentence) return;

    const requestId = ++explanationRequestRef.current;
    setExplanationLoadingIndex(index);
    setExplanationError("");

    try {
      const result = await explainSentence({
        text: sentence.characters.map((character) => character.text).join(""),
        contextBefore: sentences[index - 1]?.characters.map((character) => character.text).join("") ?? "",
        contextAfter: sentences[index + 1]?.characters.map((character) => character.text).join("") ?? "",
        language: selectedBookLanguage,
      });
      if (!result.translation || !result.reading || !result.grammar || !Array.isArray(result.vocabulary)) {
        throw new Error("A análise recebida está incompleta. Tente novamente.");
      }

      setSentenceExplanations((current) => ({
        ...current,
        [index]: {
          translation: result.translation,
          reading: result.reading,
          grammar: result.grammar,
          vocabulary: result.vocabulary,
        },
      }));
      if (explanationRequestRef.current === requestId) setExplanationLoadingIndex(null);
    } catch (error) {
      if (explanationRequestRef.current === requestId) {
        setExplanationLoadingIndex(null);
        setExplanationError(error instanceof Error ? error.message : "Não foi possível analisar este trecho.");
      }
    }
  }

  useEffect(() => {
    hasCenteredSentenceRef.current = false;
    if (scrollAnimationRef.current !== null) cancelAnimationFrame(scrollAnimationRef.current);
    scrollAnimationRef.current = null;
    sentenceAnimationRef.current?.cancel();
    sentenceAnimationRef.current = null;
  }, [alignment]);

  useEffect(() => {
    if (view !== "reader" || !autoScroll || activeSentenceIndex < 0 || explainedSentenceIndex !== null) return;
    const animate = hasCenteredSentenceRef.current;
    const frame = requestAnimationFrame(() => {
      centerSentence(activeSentenceIndex, animate);
      hasCenteredSentenceRef.current = true;
    });
    return () => cancelAnimationFrame(frame);
  }, [activeSentenceIndex, autoScroll, centerSentence, explainedSentenceIndex, sentences, view]);

  useEffect(() => {
    if (view !== "reader" || !autoScroll || activeSentenceIndex < 0 || explainedSentenceIndex !== null) return;
    let resizeFrame = 0;
    let hasObservedInitialLayout = false;
    const recenter = () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => centerSentence(activeSentenceIndex, false));
    };
    const resizeObserver = new ResizeObserver(() => {
      if (!hasObservedInitialLayout) {
        hasObservedInitialLayout = true;
        return;
      }
      recenter();
    });
    const activeSentence = sentenceRefs.current[activeSentenceIndex];
    [sentenceViewportRef.current, activeSentence, readerTopbarRef.current, playerBarRef.current]
      .forEach((element) => { if (element) resizeObserver.observe(element); });
    window.addEventListener("resize", recenter);
    return () => {
      window.removeEventListener("resize", recenter);
      resizeObserver.disconnect();
      cancelAnimationFrame(resizeFrame);
    };
  }, [activeSentenceIndex, autoScroll, centerSentence, explainedSentenceIndex, view]);

  useEffect(() => () => {
    if (scrollAnimationRef.current !== null) cancelAnimationFrame(scrollAnimationRef.current);
    if (explanationRevealFrameRef.current !== null) cancelAnimationFrame(explanationRevealFrameRef.current);
    sentenceAnimationRef.current?.cancel();
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  useEffect(() => {
    if (view !== "reader") return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code === "Escape" && explainedSentenceIndex !== null) {
        event.preventDefault();
        closeSentenceExplanation();
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target?.matches("input, textarea, select, button") || target?.closest(".sentence-explanation")) return;
      if (event.code === "Space") {
        event.preventDefault();
        void togglePlay();
      } else if (event.code === "ArrowLeft") {
        event.preventDefault();
        seek(currentTime - 5);
      } else if (event.code === "ArrowRight") {
        event.preventDefault();
        seek(currentTime + 5);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeSentenceExplanation, currentTime, explainedSentenceIndex, seek, togglePlay, view]);

  async function handleNewBookFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setNewBookFile(file);
    setNewBookData(null);
    setLibraryError("");
    setIsBookParsing(true);
    try {
      const extracted = await extractBook(file);
      setNewBookData(extracted);
      setNewBookTitle(extracted.title);
      setNewBookAuthor(extracted.author);
      setNewBookLanguage(extracted.language);
    } catch (error) {
      setLibraryError(error instanceof Error ? error.message : "Não consegui ler este livro.");
    } finally {
      setIsBookParsing(false);
    }
  }

  async function createLocalBook() {
    if (!newBookFile || !newBookData || !newBookTitle.trim()) return;
    setIsBookSaving(true);
    setLibraryError("");
    try {
      const now = Date.now();
      const bookId = makeId("book");
      const usableChapters = newBookData.chapters.filter((chapter) => chapter.text.trim());
      const chapters: StoredChapter[] = usableChapters.map((chapter, index) => ({
        id: makeId("chapter"),
        bookId,
        order: index,
        title: chapter.title || `Capítulo ${index + 1}`,
        text: chapter.text,
        audioBlob: null,
        audioName: "",
        audioType: "",
        audioSize: 0,
        audioLastModified: 0,
        duration: 0,
        alignment: [],
        alignmentFingerprint: "",
        status: "draft",
        errorMessage: "",
        position: 0,
        playbackRate: 1,
        createdAt: now,
        updatedAt: now,
      }));
      const book: StoredBook = {
        id: bookId,
        title: newBookTitle.trim(),
        author: newBookAuthor.trim(),
        sourceName: newBookFile.name,
        sourceBlob: newBookFile,
        language: newBookLanguage,
        currentChapterId: chapters[0]?.id ?? null,
        createdAt: now,
        updatedAt: now,
      };
      await saveBookWithChapters(book, chapters);
      await refreshLibrary();
      setSelectedBookId(bookId);
      setView("book");
      resetBookDialog();
    } catch (error) {
      setLibraryError(error instanceof Error ? error.message : "Não foi possível salvar este livro.");
    } finally {
      setIsBookSaving(false);
    }
  }

  function resetBookDialog() {
    setBookDialogOpen(false);
    setNewBookFile(null);
    setNewBookData(null);
    setNewBookTitle("");
    setNewBookAuthor("");
    setNewBookLanguage("ja");
  }

  function openBookDialog() {
    setLibraryError("");
    if (libraryLanguage !== "all") setNewBookLanguage(libraryLanguage);
    setBookDialogOpen(true);
  }

  function openBook(book: LibraryBook) {
    setSelectedBookId(book.id);
    setView("book");
    setLibraryError("");
  }

  async function prepareChapter(book: LibraryBook, chapterSummary: StoredChapter | null) {
    const chapter = chapterSummary ? await getStoredChapter(chapterSummary.id) ?? chapterSummary : null;
    const order = chapter?.order ?? book.chapters.length;
    const storedAudio = chapter?.audioBlob
      ? new File([chapter.audioBlob], chapter.audioName || `capitulo-${order + 1}.mp3`, {
        type: chapter.audioType || chapter.audioBlob.type,
        lastModified: chapter.audioLastModified || Date.now(),
      })
      : null;
    setSelectedBookId(book.id);
    setEditingChapterId(chapter?.id ?? null);
    setBookTitle(book.title);
    setChapterTitle(chapter?.title || `Capítulo ${order + 1}`);
    setSourceText(chapter?.text ?? "");
    setAudioFile(storedAudio);
    setPendingAudioDuration(chapter?.duration ?? 0);
    setBookFile(null);
    setFormError("");
    setSetupOpen(true);
  }

  async function activateChapter(book: StoredBook | LibraryBook, chapterSummary: StoredChapter) {
    const chapter = await getStoredChapter(chapterSummary.id) ?? chapterSummary;
    if ((!chapter.audioBlob && !chapter.audioUrl) || !chapter.alignment.length) return;
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const file = chapter.audioBlob ? new File([chapter.audioBlob], chapter.audioName, {
      type: chapter.audioType || chapter.audioBlob.type,
      lastModified: chapter.audioLastModified || Date.now(),
    }) : null;
    const nextUrl = chapter.audioUrl || (file ? URL.createObjectURL(file) : "");
    if (!nextUrl) return;
    objectUrlRef.current = file ? nextUrl : null;
    activeChapterRef.current = chapter;
    setSelectedBookId(book.id);
    setActiveStoredChapterId(chapter.id);
    setBookTitle(book.title);
    setChapterTitle(chapter.title);
    setSourceText(chapter.text);
    setAudioFile(file);
    if (explanationRevealFrameRef.current !== null) cancelAnimationFrame(explanationRevealFrameRef.current);
    explanationRevealFrameRef.current = null;
    pendingExplanationIndexRef.current = null;
    sentenceAnimationRef.current?.cancel();
    sentenceAnimationRef.current = null;
    explanationViewportTopRef.current = null;
    setExplainedSentenceIndex(null);
    setSentenceExplanations({});
    setExplanationLoadingIndex(null);
    setExplanationError("");
    explanationRequestRef.current += 1;
    setAlignment(chapter.alignment);
    setAudioUrl(nextUrl);
    setAudioDuration(chapter.duration);
    setPlaybackRate(chapter.playbackRate || 1);
    setIsPlaying(false);
    setLoopSentenceIndex(null);
    setCurrentTime(chapter.position || 0);
    pendingRestoreRef.current = chapter.position || 0;
    setSidebarOpen(false);
    setFormError("");
    setView("reader");
  }

  async function handleBookChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setBookFile(file);
    setFormError("");
    setIsParsing(true);
    try {
      const extracted = await extractBook(file);
      setSourceText(extracted.chapters.length === 1 ? extracted.chapters[0].text : extracted.text);
      if (!editingChapterId) setChapterTitle(extracted.chapters[0]?.title || chapterTitleFromText(extracted.text));
    } catch (error) {
      setSourceText("");
      setFormError(error instanceof Error ? error.message : "Não consegui ler este arquivo.");
    } finally {
      setIsParsing(false);
    }
  }

  function handleAudioChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    setAudioFile(file);
    setPendingAudioDuration(0);
    setFormError("");
    if (file) void readAudioDuration(file).then(setPendingAudioDuration).catch(() => setPendingAudioDuration(0));
  }

  async function handleAlignment() {
    const targetBook = libraryBooks.find((book) => book.id === selectedBookId);
    if (!targetBook) {
      setFormError("Selecione o livro deste capítulo.");
      return;
    }
    if (!audioFile) {
      setFormError("Selecione o áudio deste capítulo.");
      return;
    }
    if (!sourceText.trim()) {
      setFormError("Importe ou cole o texto correspondente ao áudio.");
      return;
    }

    setIsProcessing(true);
    setFormError("");
    let pendingChapter: StoredChapter | null = null;
    try {
      const existing = editingChapterId
        ? targetBook.chapters.find((chapter) => chapter.id === editingChapterId) ?? null
        : null;
      const fingerprint = await fingerprintChapter(audioFile, sourceText);

      if (existing?.alignmentFingerprint === fingerprint && existing.status === "aligned" && (existing.audioBlob || existing.audioKey)) {
        await activateChapter(targetBook, existing);
        setSetupOpen(false);
        return;
      }

      const now = Date.now();
      const chapterId = existing?.id ?? makeId("chapter");
      pendingChapter = {
        id: chapterId,
        bookId: targetBook.id,
        order: existing?.order ?? targetBook.chapters.length,
        title: chapterTitle.trim() || `Capítulo ${(existing?.order ?? targetBook.chapters.length) + 1}`,
        text: sourceText.trim(),
        audioBlob: audioFile,
        audioName: audioFile.name,
        audioType: audioFile.type,
        audioSize: audioFile.size,
        audioLastModified: audioFile.lastModified,
        duration: pendingAudioDuration || existing?.duration || 0,
        alignment: [],
        alignmentFingerprint: fingerprint,
        status: "processing",
        errorMessage: "",
        position: existing?.position ?? 0,
        playbackRate: existing?.playbackRate ?? 1,
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
      };
      const updatedBook: StoredBook = {
        ...plainBook(targetBook),
        currentChapterId: chapterId,
        updatedAt: now,
      };
      pendingChapter = await saveBookAndChapter(updatedBook, pendingChapter);
      const result = await alignStoredChapter(targetBook.id, pendingChapter.id);

      const validCharacters = result.characters?.filter((character) =>
        typeof character.text === "string" && Number.isFinite(character.start) && Number.isFinite(character.end),
      );
      if (!validCharacters?.length) throw new Error("O serviço de alinhamento não devolveu marcações para este texto.");

      const alignedChapter: StoredChapter = {
        ...pendingChapter,
        alignment: validCharacters,
        duration: pendingAudioDuration || validCharacters.at(-1)?.end || 0,
        status: "aligned",
        errorMessage: "",
        updatedAt: Date.now(),
      };
      const finalBook = { ...updatedBook, updatedAt: Date.now() };
      await saveBookAndChapter(finalBook, alignedChapter);
      await refreshLibrary();
      setSetupOpen(false);
      await activateChapter(finalBook, alignedChapter);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Não foi possível concluir o alinhamento.";
      setFormError(message);
      if (pendingChapter) {
        const failedChapter: StoredChapter = {
          ...pendingChapter,
          status: "error",
          errorMessage: message,
          updatedAt: Date.now(),
        };
        await saveChapter(failedChapter).catch(() => undefined);
        await refreshLibrary();
      }
    } finally {
      setIsProcessing(false);
    }
  }

  async function handleDeleteBook(book: LibraryBook) {
    if (!window.confirm(`Excluir “${book.title}” e todos os capítulos e áudios associados?`)) return;
    await deleteStoredBook(book.id);
    if (selectedBookId === book.id) setSelectedBookId(null);
    setView("library");
    await refreshLibrary();
  }

  async function handleDeleteChapter(chapter: StoredChapter) {
    if (!window.confirm(`Excluir “${chapter.title}” e o áudio associado?`)) return;
    await deleteStoredChapter(chapter.id);
    await refreshLibrary();
  }

  function handleLoadedMetadata() {
    const audio = audioRef.current;
    if (!audio) return;
    const measuredDuration = Number.isFinite(audio.duration) ? audio.duration : 0;
    setAudioDuration(measuredDuration);
    if (activeChapterRef.current && Math.abs(activeChapterRef.current.duration - measuredDuration) > 0.5) {
      const updated = { ...activeChapterRef.current, duration: measuredDuration, updatedAt: Date.now() };
      activeChapterRef.current = updated;
      void saveChapterProgress(updated);
    }
    if (pendingRestoreRef.current > 0 && pendingRestoreRef.current < audio.duration) {
      audio.currentTime = pendingRestoreRef.current;
      setCurrentTime(pendingRestoreRef.current);
    }
    pendingRestoreRef.current = 0;
  }

  function cycleSpeed() {
    const index = SPEEDS.indexOf(playbackRate);
    const nextRate = SPEEDS[(index + 1) % SPEEDS.length];
    setPlaybackRate(nextRate);
    if (activeChapterRef.current) {
      const updated = { ...activeChapterRef.current, playbackRate: nextRate, updatedAt: Date.now() };
      activeChapterRef.current = updated;
      void saveChapterProgress(updated);
    }
  }

  function toggleSentenceLoop() {
    setLoopSentenceIndex((current) => current === null && activeSentenceIndex >= 0 ? activeSentenceIndex : null);
  }

  function handleAudioEnded() {
    const audio = audioRef.current;
    const loopedSentence = loopSentenceIndex === null ? null : sentences[loopSentenceIndex];
    if (audio && loopedSentence) {
      audio.currentTime = loopedSentence.start;
      setCurrentTime(loopedSentence.start);
      void audio.play().catch(() => setFormError("O navegador não conseguiu reiniciar este trecho."));
      return;
    }
    setIsPlaying(false);
    savePosition(duration);
  }

  function leaveReader(nextView: "library" | "book") {
    if (audioRef.current) audioRef.current.pause();
    savePosition(audioRef.current?.currentTime ?? currentTime);
    setView(nextView);
    setSidebarOpen(false);
    void refreshLibrary();
  }

  function createFlashcardFromSentence(sentence: ReaderSentence, explanation: SentenceExplanation) {
    if (audioRef.current) audioRef.current.pause();
    const sentenceText = sentence.characters.map((character) => character.text).join("").trim();
    const vocabulary = explanation.vocabulary.length
      ? `<hr><strong>Vocabulário</strong><ul>${explanation.vocabulary.map((item) => `<li><b>${escapeFlashcardHtml(item.term)}</b> · ${escapeFlashcardHtml(item.reading)} — ${escapeFlashcardHtml(item.meaning)}</li>`).join("")}</ul>`
      : "";
    setFlashcardSeed({
      front: escapeFlashcardHtml(sentenceText),
      back: `<strong>${escapeFlashcardHtml(explanation.translation)}</strong><hr>${escapeFlashcardHtml(explanation.reading)}<br><br>${escapeFlashcardHtml(explanation.grammar)}${vocabulary}`,
      tags: ["japa-reader", selectedBookLanguage, `livro-${slugTag(bookTitle)}`, `capitulo-${Math.max(1, activeChapterIndex + 1)}`],
      source: {
        bookId: selectedBook?.id,
        chapterId: activeStoredChapterId || undefined,
        sentenceStart: sentence.start,
        sentenceEnd: sentence.end,
      },
    });
    setView("flashcards");
  }

  function createFlashcardFromVocabulary(
    sentence: ReaderSentence,
    item: SentenceExplanation["vocabulary"][number],
  ) {
    if (audioRef.current) audioRef.current.pause();
    setIsPlaying(false);
    setVocabularyFlashcardContext({
      term: item.term,
      reading: item.reading,
      meaning: item.meaning,
      sentence: sentence.characters.map((character) => character.text).join("").trim(),
      language: selectedBookLanguage,
      tags: [
        "japa-reader",
        "vocabulario",
        selectedBookLanguage,
        `livro-${slugTag(bookTitle)}`,
        `capitulo-${Math.max(1, activeChapterIndex + 1)}`,
      ],
      source: {
        bookId: selectedBook?.id,
        chapterId: activeStoredChapterId || undefined,
        sentenceStart: sentence.start,
        sentenceEnd: sentence.end,
      },
    });
  }

  const appHeader = (context: "library" | "book" | "flashcards") => (
    <header className="topbar collection-topbar">
      <div className="topbar-leading">
        {context === "book" && (
          <button className="icon-button" onClick={() => setView("library")} aria-label="Voltar para a biblioteca">
            <ArrowLeft size={19} />
          </button>
        )}
        <button className="brand brand-button" onClick={() => setView("library")} aria-label="Abrir biblioteca">
          <span className="brand-mark" aria-hidden="true">読</span>
          <span>JAPA <strong>READER</strong></span>
        </button>
      </div>
      <div className="collection-heading">
        <span>{context === "library" ? "Sua biblioteca" : context === "flashcards" ? "Flashcards" : selectedBook?.title}</span>
        <small>{context === "library" ? "Livros e audiobooks neste dispositivo" : context === "flashcards" ? "Revisão espaçada com FSRS" : selectedBook?.author || "Autor não informado"}</small>
      </div>
      <div className="topbar-actions">
        {context === "library" ? (
          <>
            <button className="section-switch-button" onClick={() => setView("flashcards")} aria-label="Abrir Flashcards">
              <Layers3 size={17} /><span>Flashcards</span>
            </button>
            <button className="import-button" onClick={openBookDialog} aria-label="Adicionar livro">
              <Plus size={17} /><span>Adicionar livro</span>
            </button>
          </>
        ) : context === "flashcards" ? (
          <button className="section-switch-button" onClick={() => setView("library")} aria-label="Abrir biblioteca">
            <Library size={17} /><span>Biblioteca</span>
          </button>
        ) : (
          <button className="import-button" onClick={() => selectedBook && void prepareChapter(selectedBook, null)} aria-label="Adicionar capítulo">
            <Plus size={17} /><span>Adicionar capítulo</span>
          </button>
        )}
        <button className="account-button" onClick={onSignOut} aria-label={`Sair da conta de ${user.name}`} title={`${user.name} · sair`}>
          {user.picture ? <img src={user.picture} alt="" referrerPolicy="no-referrer" /> : <span>{user.name.slice(0, 1).toUpperCase()}</span>}
          <LogOut size={14} />
        </button>
      </div>
    </header>
  );

  return (
    <main className={`app-shell view-${view} language-${selectedBookLanguage}`}>
      {view === "library" && (
        <>
          {appHeader("library")}
          <section className="library-page" aria-labelledby="library-title">
            <div className="library-hero">
              <div>
                <span className="eyebrow">SUA BIBLIOTECA</span>
                <h1 id="library-title">Seus livros, capítulo por capítulo.</h1>
                <p>Importe o EPUB uma vez, associe cada faixa de áudio e continue exatamente de onde parou.</p>
              </div>
              <div className="library-summary" aria-label="Resumo da biblioteca">
                <strong>{visibleLibraryBooks.length}</strong>
                <span>{visibleLibraryBooks.length === 1 ? "livro" : "livros"}</span>
                <i />
                <strong>{visibleLibraryBooks.reduce((total, book) => total + book.chapters.length, 0)}</strong>
                <span>capítulos</span>
              </div>
            </div>

            <nav className="language-switch" aria-label="Idioma da biblioteca">
              <button className={libraryLanguage === "all" ? "is-active" : ""} onClick={() => setLibraryLanguage("all")} aria-pressed={libraryLanguage === "all"}>
                <span>Todos</span><small>{libraryBooks.length}</small>
              </button>
              {BOOK_LANGUAGES.map((language) => {
                const count = libraryBooks.filter((book) => normalizeBookLanguage(book.language) === language.code).length;
                return (
                  <button key={language.code} className={libraryLanguage === language.code ? "is-active" : ""} onClick={() => setLibraryLanguage(language.code)} aria-pressed={libraryLanguage === language.code}>
                    <span>{language.label}</span><small>{count}</small>
                  </button>
                );
              })}
            </nav>

            {libraryError && <div className="form-error library-error" role="alert">{libraryError}</div>}
            {!libraryReady ? (
              <div className="library-loading"><LoaderCircle className="spin" size={22} /> Abrindo sua biblioteca…</div>
            ) : visibleLibraryBooks.length ? (
              <div className="book-grid">
                {visibleLibraryBooks.map((book) => (
                  <article className={`book-card language-${normalizeBookLanguage(book.language)}`} key={book.id}>
                    <button className="book-card-main" onClick={() => openBook(book)}>
                      <span className="book-cover" aria-hidden="true"><i>{coverGlyph(book.title)}</i><small>{bookLanguageMeta(book.language).code.toUpperCase()}</small></span>
                      <span className="book-card-copy">
                        <small>{book.author || "Autor não informado"}<i>{bookLanguageMeta(book.language).nativeLabel}</i></small>
                        <strong>{book.title}</strong>
                        <span>{book.chapters.length} {book.chapters.length === 1 ? "capítulo" : "capítulos"} · {formatDuration(book.totalDuration)}</span>
                      </span>
                      <span className="book-progress">
                        <i><b style={{ width: `${Math.round(book.progress * 100)}%` }} /></i>
                        <small>{Math.round(book.progress * 100)}%</small>
                      </span>
                    </button>
                    <div className="book-card-actions">
                      <button onClick={() => openBook(book)}><BookOpen size={15} /> Ver capítulos</button>
                      <button className="danger-action" onClick={() => void handleDeleteBook(book)} aria-label={`Excluir ${book.title}`}><Trash2 size={15} /></button>
                    </div>
                  </article>
                ))}
                <button className="new-book-card" onClick={openBookDialog}>
                  <span><Plus size={22} /></span>
                  <strong>Adicionar outro livro</strong>
                  <small>EPUB ou TXT</small>
                </button>
              </div>
            ) : libraryBooks.length ? (
              <div className="empty-library language-empty">
                <span className="empty-library-icon"><Library size={28} /></span>
                <h2>Nenhum livro em {bookLanguageMeta(libraryLanguage).label.toLowerCase()}</h2>
                <p>Adicione um livro neste idioma ou escolha outra biblioteca acima.</p>
                <button className="align-button" onClick={openBookDialog}><Plus size={18} /> Adicionar livro</button>
              </div>
            ) : (
              <div className="empty-library">
                <span className="empty-library-icon"><Library size={28} /></span>
                <h2>Sua estante está vazia</h2>
                <p>Comece importando um EPUB. O Japa Reader separa as partes do livro e prepara a organização dos capítulos.</p>
                <button className="align-button" onClick={openBookDialog}><Plus size={18} /> Adicionar primeiro livro</button>
              </div>
            )}
          </section>
        </>
      )}

      {view === "flashcards" && (
        <>
          {appHeader("flashcards")}
          <Flashcards initialDraft={flashcardSeed} onInitialDraftConsumed={() => setFlashcardSeed(null)} />
        </>
      )}

      {view === "book" && selectedBook && (
        <>
          {appHeader("book")}
          <section className="book-page" aria-labelledby="book-page-title">
            <aside className="book-profile">
              <span className="book-cover book-cover-large" aria-hidden="true"><i>{coverGlyph(selectedBook.title)}</i><small>JAPA READER</small></span>
              <span className="eyebrow">LIVRO NA NUVEM</span>
              <span className="book-language-badge">{bookLanguageMeta(selectedBook.language).nativeLabel}</span>
              <h1 id="book-page-title">{selectedBook.title}</h1>
              <p><UserRound size={14} /> {selectedBook.author || "Autor não informado"}</p>
               <p><Cloud size={14} /> {selectedBook.sourceName}</p>
              <div className="profile-progress">
                <span><b>{Math.round(selectedBook.progress * 100)}%</b> concluído</span>
                <i><b style={{ width: `${Math.round(selectedBook.progress * 100)}%` }} /></i>
              </div>
              <button className="danger-text-button" onClick={() => void handleDeleteBook(selectedBook)}><Trash2 size={15} /> Excluir livro</button>
            </aside>

            <div className="chapter-catalog">
              <div className="catalog-heading">
                <div>
                  <span className="eyebrow">CONTEÚDO</span>
                  <h2>{selectedBook.chapters.length} {selectedBook.chapters.length === 1 ? "capítulo" : "capítulos"}</h2>
                </div>
                <div className="catalog-stats">
                  <span><Check size={14} /> {selectedBook.chapters.filter((chapter) => chapter.status === "aligned").length} sincronizados</span>
                  <span><Clock3 size={14} /> {formatDuration(selectedBook.totalDuration)}</span>
                </div>
              </div>

              <div className="chapter-list">
                {selectedBook.chapters.map((chapter, index) => {
                  const progress = chapterProgress(chapter);
                   const aligned = chapter.status === "aligned" && Boolean(chapter.audioBlob || chapter.audioKey);
                   return (
                     <article className={`chapter-row status-${chapter.status}`} key={chapter.id}>
                       <button
                         className="chapter-card-hitbox"
                         onClick={() => aligned ? void activateChapter(selectedBook, chapter) : void prepareChapter(selectedBook, chapter)}
                         aria-label={aligned ? `Abrir ${chapter.title} no leitor` : `Preparar ${chapter.title}`}
                       />
                       <span className="chapter-number">{String(index + 1).padStart(2, "0")}</span>
                      <div className="chapter-row-main">
                        <span className="chapter-status">{statusIcon(chapter)} {statusLabel(chapter)}</span>
                        <h3>{chapter.title}</h3>
                        <p>
                           {chapter.audioName ? <><Disc3 size={13} /> {chapter.audioName}</> : <><FileText size={13} /> {(chapter.textLength ?? chapter.text.length).toLocaleString("pt-BR")} caracteres</>}
                          {chapter.duration > 0 && <><i /> {formatDuration(chapter.duration)}</>}
                        </p>
                        {aligned && <span className="chapter-progress"><i><b style={{ width: `${Math.round(progress * 100)}%` }} /></i><small>{Math.round(progress * 100)}%</small></span>}
                      </div>
                      <div className="chapter-actions">
                        {aligned && <button className="chapter-primary" onClick={() => void activateChapter(selectedBook, chapter)}>{progress > 0 ? "Continuar" : "Ler"} <Play size={14} /></button>}
                         <button className={aligned ? "chapter-secondary" : "chapter-primary"} onClick={() => void prepareChapter(selectedBook, chapter)}>
                          {aligned ? "Editar" : chapter.status === "error" ? "Tentar novamente" : "Preparar"}
                        </button>
                        <button className="icon-button danger-action" onClick={() => void handleDeleteChapter(chapter)} aria-label={`Excluir ${chapter.title}`}><Trash2 size={15} /></button>
                      </div>
                    </article>
                  );
                })}
                {!selectedBook.chapters.length && (
                  <div className="empty-chapters"><FileText size={24} /><p>Este livro ainda não tem capítulos.</p></div>
                )}
              </div>
               <button className="add-chapter-row" onClick={() => void prepareChapter(selectedBook, null)}><Plus size={18} /> Adicionar capítulo manualmente</button>
            </div>
          </section>
        </>
      )}

      {view === "reader" && (
        <>
          <header className="topbar reader-topbar" ref={readerTopbarRef}>
            <button className="icon-button mobile-menu" onClick={() => setSidebarOpen(true)} aria-label="Abrir capítulos do livro"><Menu size={19} /></button>
            <button className="brand brand-button" onClick={() => leaveReader("library")} aria-label="Abrir biblioteca">
              <span className="brand-mark" aria-hidden="true">読</span><span>JAPA <strong>READER</strong></span>
            </button>
            <div className="book-heading"><span>{bookTitle}</span><small>{chapterTitle}</small></div>
            <button className="import-button" onClick={() => leaveReader("book")} aria-label="Ver capítulos"><BookOpen size={17} /><span>Ver capítulos</span></button>
          </header>

          <div className="reading-layout" id="reader">
            <aside className={`timeline-panel chapter-sidebar ${sidebarOpen ? "is-open" : ""}`} aria-label="Capítulos do livro">
              <button className="icon-button close-sidebar" onClick={() => setSidebarOpen(false)} aria-label="Fechar navegação"><X size={18} /></button>
              <div className="timeline-book"><Library size={18} /><span>Livro</span><strong>Capítulos</strong></div>
              <div className="reader-chapter-list">
                {selectedBook?.chapters.map((chapter, index) => {
                  const available = chapter.status === "aligned" && Boolean(chapter.audioBlob || chapter.audioKey);
                  return (
                    <button
                      className={`reader-chapter-item ${chapter.id === activeStoredChapterId ? "is-active" : ""}`}
                      key={chapter.id}
                      onClick={() => available ? void activateChapter(selectedBook, chapter) : leaveReader("book")}
                    >
                      <span>{String(index + 1).padStart(2, "0")}</span>
                      <strong>{chapter.title}</strong>
                      <small>{available ? `${Math.round(chapterProgress(chapter) * 100)}%` : "Preparar"}</small>
                    </button>
                  );
                })}
              </div>
              <button className="sidebar-book-button" onClick={() => leaveReader("book")}><ArrowLeft size={14} /> Voltar ao livro</button>
            </aside>

            <section className="reader-canvas" aria-label="Texto sincronizado">
              <div className="reader-heading">
                <div>
                  <span className="eyebrow">CAPÍTULO {String(Math.max(0, activeChapterIndex) + 1).padStart(2, "0")} · LEITURA SINCRONIZADA</span>
                  <h1>{chapterTitle}</h1>
                </div>
                <div className="reader-status"><span className={isPlaying ? "status-live" : ""} />{isPlaying ? "Narrando" : "Pausado"}</div>
              </div>

              <div className={`sentence-viewport ${explainedSentenceIndex !== null ? "is-explaining" : ""}`} ref={sentenceViewportRef}>
                <article className={`sentence-list ${explainedSentenceIndex !== null ? "is-explaining" : ""}`} lang={selectedBookLanguage}>
                  {sentences.map((sentence, sentenceIndex) => {
                    const active = sentenceIndex === activeSentenceIndex;
                    const complete = sentenceIndex < activeSentenceIndex;
                    const next = sentenceIndex === activeSentenceIndex + 1;
                    const explained = sentenceIndex === explainedSentenceIndex;
                    const explanation = sentenceExplanations[sentenceIndex];
                    const explanationLoading = explanationLoadingIndex === sentenceIndex;
                    return (
                      <Fragment key={sentence.id}>
                        <div
                          className={`reader-sentence ${active ? "is-active" : ""} ${complete ? "is-complete" : ""} ${next ? "is-next" : ""} ${explained ? "is-explained" : ""}`}
                          ref={(node) => { sentenceRefs.current[sentenceIndex] = node; }}
                        >
                          <button
                            className="sentence-explain-trigger"
                            onClick={() => toggleSentenceExplanation(sentenceIndex)}
                            aria-expanded={explained}
                            aria-controls={`sentence-explanation-${sentence.id}`}
                            aria-label={`${explained ? "Fechar explicação" : "Explicar trecho"} de ${formatTime(sentence.start)}`}
                          >
                            {explained ? <X size={18} /> : <Sparkles size={19} />}
                          </button>
                          <button className="sentence-time" onClick={() => seek(sentence.start)} aria-label={`Ouvir a partir de ${formatTime(sentence.start)}`}>{formatTime(sentence.start)}</button>
                          <div className="sentence-rail" aria-hidden="true"><Sparkles size={14} /><span /></div>
                          <p onClick={() => seek(sentence.start)}>
                            {sentence.characters.map((character, localIndex) => {
                              const globalIndex = sentence.globalStart + localIndex;
                              const state = active
                                ? globalIndex < activeCharacterIndex ? "is-sung" : globalIndex === activeCharacterIndex ? "is-current" : "is-upcoming"
                                : "";
                              return <span className={`karaoke-char ${state}`} key={globalIndex}>{character.text}</span>;
                            })}
                          </p>
                        </div>

                        {explained && (
                          <section
                            className="sentence-explanation"
                            id={`sentence-explanation-${sentence.id}`}
                            lang="pt-BR"
                            tabIndex={0}
                            onFocus={() => {
                              const viewport = sentenceViewportRef.current;
                              const lockedTop = explanationViewportTopRef.current;
                              if (viewport && lockedTop !== null) viewport.scrollTop = lockedTop;
                            }}
                            onKeyDown={(event) => {
                              const viewport = sentenceViewportRef.current;
                              const lockedTop = explanationViewportTopRef.current;
                              if (viewport && lockedTop !== null) viewport.scrollTop = lockedTop;
                              const amount = Math.max(80, event.currentTarget.clientHeight * 0.78);
                              if (event.key === "ArrowDown" || event.key === "PageDown") {
                                event.preventDefault();
                                event.currentTarget.scrollBy({ top: event.key === "ArrowDown" ? 64 : amount, behavior: "smooth" });
                              } else if (event.key === "ArrowUp" || event.key === "PageUp") {
                                event.preventDefault();
                                event.currentTarget.scrollBy({ top: event.key === "ArrowUp" ? -64 : -amount, behavior: "smooth" });
                              } else if (event.key === "Home" || event.key === "End") {
                                event.preventDefault();
                                event.currentTarget.scrollTo({ top: event.key === "Home" ? 0 : event.currentTarget.scrollHeight, behavior: "smooth" });
                              }
                            }}
                            aria-label={`Explicação do trecho de ${formatTime(sentence.start)}`}
                          >
                            <header className="explanation-header">
                              <div>
                                <span className="explanation-kicker"><Sparkles size={12} /> Análise do trecho</span>
                                <h2>Entenda este trecho</h2>
                              </div>
                            </header>

                            {explanationLoading && (
                              <div className="explanation-loading" role="status">
                                <LoaderCircle className="spin" size={19} />
                                <span>Analisando tradução, leitura e gramática…</span>
                              </div>
                            )}

                            {!explanationLoading && explanationError && !explanation && (
                              <div className="explanation-error" role="alert">
                                <span>{explanationError}</span>
                                <button onClick={() => void loadSentenceExplanation(sentenceIndex)}>Tentar novamente</button>
                              </div>
                            )}

                            {explanation && (
                              <>
                                <div className="explanation-table-wrap">
                                  <table className="explanation-analysis-table">
                                    <tbody>
                                      <tr>
                                        <th scope="row">Tradução natural</th>
                                        <td>{explanation.translation}</td>
                                      </tr>
                                      <tr>
                                        <th scope="row">{selectedBookLanguage === "ja" ? "Como ler" : "Pronúncia"}</th>
                                        <td>{explanation.reading}</td>
                                      </tr>
                                      <tr>
                                        <th scope="row">Ponto de gramática</th>
                                        <td>{explanation.grammar}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>

                                <div className="explanation-vocabulary-table-wrap">
                                  <span className="explanation-table-title">Vocabulário em destaque</span>
                                  <table className="explanation-vocabulary-table">
                                    <thead>
                                      <tr>
                                        <th scope="col">Termo</th>
                                        <th scope="col">Leitura</th>
                                        <th scope="col">Significado</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {explanation.vocabulary.map((item) => (
                                        <tr key={`${item.term}-${item.reading}`}>
                                          <td>
                                            <button
                                              type="button"
                                              className="vocabulary-term-button"
                                              onClick={() => createFlashcardFromVocabulary(sentence, item)}
                                              aria-label={`Criar flashcard com ${item.term}`}
                                              title="Criar flashcard com esta palavra"
                                            >
                                              <strong lang={selectedBookLanguage}>{item.term}</strong><Plus size={14} />
                                            </button>
                                          </td>
                                          <td>{item.reading}</td>
                                          <td>{item.meaning}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </>
                            )}

                            <footer className="explanation-footer">
                              <button onClick={() => void playFrom(sentence.start)}><Play size={14} fill="currentColor" /> Ouvir trecho</button>
                              {explanation && <button onClick={() => createFlashcardFromSentence(sentence, explanation)}><Layers3 size={14} /> Criar flashcard</button>}
                              <small>Explicação gerada sob demanda.</small>
                            </footer>
                          </section>
                        )}
                      </Fragment>
                    );
                  })}
                </article>
              </div>
            </section>
          </div>

          <footer className="player-bar" ref={playerBarRef} aria-label="Controles do audiobook">
            <div className="player-meta"><span className="cover-mini" aria-hidden="true">日</span><div><strong>{bookTitle}</strong><span>{audioFile?.name}</span></div></div>
            <div className="transport">
              <button className="icon-button" onClick={() => seek(currentTime - 10)} aria-label="Voltar 10 segundos"><ChevronLeft size={20} /></button>
              <button className="play-button" onClick={() => void togglePlay()} aria-label={isPlaying ? "Pausar" : "Reproduzir"}>{isPlaying ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" />}</button>
              <button className="icon-button" onClick={() => seek(currentTime + 10)} aria-label="Avançar 10 segundos"><ChevronRight size={20} /></button>
              <button
                className={`icon-button loop-button ${loopSentenceIndex !== null ? "is-on" : ""}`}
                onClick={toggleSentenceLoop}
                aria-label={loopSentenceIndex !== null ? "Desativar repetição do trecho" : "Repetir trecho atual"}
                aria-pressed={loopSentenceIndex !== null}
                title={loopSentenceIndex !== null ? "Trecho em repetição" : "Repetir trecho atual"}
              >
                <Repeat2 size={18} />
              </button>
            </div>
            <div className="progress-wrap">
              <time>{formatTime(currentTime)}</time>
              <input className="progress-slider" type="range" min="0" max={Math.max(duration, 0.01)} step="0.01" value={Math.min(currentTime, duration || 0)} onChange={(event) => seek(Number(event.target.value))} aria-label="Posição da reprodução" style={{ "--progress": `${duration ? (currentTime / duration) * 100 : 0}%` } as React.CSSProperties} />
              <time>{formatTime(duration)}</time>
            </div>
            <div className="player-options">
              <button className={`text-button ${autoScroll ? "is-on" : ""}`} onClick={() => setAutoScroll((value) => !value)} title="Rolagem automática"><RotateCcw size={15} /><span>Auto</span></button>
              <button className="speed-button" onClick={cycleSpeed} aria-label={`Velocidade ${playbackRate} vezes`}><Gauge size={15} /> {playbackRate}×</button>
              <button className="icon-button" onClick={() => setIsMuted((value) => !value)} aria-label={isMuted ? "Ativar som" : "Silenciar"}>{isMuted ? <VolumeX size={19} /> : <Volume2 size={19} />}</button>
            </div>
          </footer>

          {audioUrl && <audio ref={audioRef} src={audioUrl} onLoadedMetadata={handleLoadedMetadata} onPlay={() => setIsPlaying(true)} onPause={() => { setIsPlaying(false); savePosition(audioRef.current?.currentTime ?? currentTime); }} onEnded={handleAudioEnded} />}
        </>
      )}

      {vocabularyFlashcardContext && (
        <VocabularyFlashcardDialog
          context={vocabularyFlashcardContext}
          onClose={() => setVocabularyFlashcardContext(null)}
        />
      )}

      {bookDialogOpen && (
        <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target && !isBookParsing && !isBookSaving) resetBookDialog(); }}>
          <section className="import-dialog book-dialog" role="dialog" aria-modal="true" aria-labelledby="book-dialog-title">
            <div className="dialog-header">
              <div><span className="eyebrow">NOVO LIVRO</span><h2 id="book-dialog-title">Adicionar à biblioteca</h2><p>O arquivo fica somente neste dispositivo. O Japa Reader usa a estrutura do EPUB para organizar os capítulos.</p></div>
              <button className="icon-button" onClick={resetBookDialog} disabled={isBookParsing || isBookSaving} aria-label="Fechar"><X size={20} /></button>
            </div>
            <label className={`drop-field book-drop-field ${newBookFile ? "has-file" : ""}`}>
              <input type="file" accept=".epub,.txt,text/plain,application/epub+zip" onChange={handleNewBookFile} />
              <span className="drop-icon"><BookOpen size={22} /></span>
              <span><strong>{isBookParsing ? "Lendo a estrutura do livro…" : newBookFile?.name || "Escolha um EPUB ou TXT"}</strong><small>{newBookData ? `${newBookData.chapters.length} partes encontradas · ${newBookData.text.length.toLocaleString("pt-BR")} caracteres` : "O arquivo original será guardado com segurança na sua conta"}</small></span>
              {isBookParsing ? <LoaderCircle className="spin" size={18} /> : <UploadCloud size={18} />}
            </label>
            <div className="metadata-grid">
              <label><span>Título</span><input value={newBookTitle} onChange={(event) => setNewBookTitle(event.target.value)} placeholder="Título do livro" /></label>
              <label><span>Autor</span><input value={newBookAuthor} onChange={(event) => setNewBookAuthor(event.target.value)} placeholder="Autor (opcional)" /></label>
              <label className="metadata-language"><span>Idioma do livro</span><select value={newBookLanguage} onChange={(event) => setNewBookLanguage(event.target.value as BookLanguage)}>{BOOK_LANGUAGES.map((language) => <option key={language.code} value={language.code}>{language.label} · {language.nativeLabel}</option>)}</select><small>Detectado automaticamente; você pode corrigir antes de criar.</small></label>
            </div>
            {libraryError && <div className="form-error" role="alert">{libraryError}</div>}
            <div className="dialog-footer"><p><Cloud size={15} /> Biblioteca privada vinculada à sua conta.</p><button className="align-button" onClick={() => void createLocalBook()} disabled={!newBookData || !newBookTitle.trim() || isBookParsing || isBookSaving}>{isBookSaving ? <LoaderCircle className="spin" size={18} /> : <Plus size={18} />}{isBookSaving ? "Salvando livro…" : "Criar livro"}</button></div>
          </section>
        </div>
      )}

      {setupOpen && selectedBook && (
        <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target && !isProcessing) setSetupOpen(false); }}>
          <section className="import-dialog" role="dialog" aria-modal="true" aria-labelledby="import-title">
            <div className="dialog-header">
              <div><span className="eyebrow">{editingChapterId ? "PREPARAR CAPÍTULO" : "NOVO CAPÍTULO"}</span><h2 id="import-title">Texto e narração</h2><p>Revise o trecho, associe uma faixa de áudio e crie a leitura sincronizada.</p></div>
              <button className="icon-button" onClick={() => setSetupOpen(false)} disabled={isProcessing} aria-label="Fechar"><X size={20} /></button>
            </div>
            <label className="chapter-title-field"><span>Título do capítulo</span><input value={chapterTitle} onChange={(event) => setChapterTitle(event.target.value)} placeholder="Capítulo 1" /></label>
            <div className="steps-row" aria-hidden="true">
              <span className={sourceText ? "is-done" : "is-current"}>{sourceText ? <Check size={14} /> : "1"} Texto</span><i />
              <span className={audioFile ? "is-done" : sourceText ? "is-current" : ""}>{audioFile ? <Check size={14} /> : "2"} Áudio</span><i />
              <span className={audioFile && sourceText ? "is-current" : ""}>3 Alinhar</span>
            </div>
            <div className="import-grid">
              <label className={`drop-field ${bookFile || sourceText ? "has-file" : ""}`}>
                <input type="file" accept=".epub,.txt,text/plain,application/epub+zip" onChange={handleBookChange} />
                <span className="drop-icon"><FileText size={21} /></span>
                <span><strong>{isParsing ? "Extraindo o texto…" : bookFile?.name || (sourceText ? "Texto do EPUB" : "EPUB ou TXT")}</strong><small>{sourceText ? `${sourceText.length.toLocaleString("pt-BR")} caracteres` : "Selecione o texto deste capítulo"}</small></span>
                {isParsing ? <LoaderCircle className="spin" size={18} /> : <UploadCloud size={18} />}
              </label>
              <label className={`drop-field ${audioFile ? "has-file" : ""}`}>
                <input type="file" accept="audio/*,.mp3,.m4a,.wav,.aac,.flac,.ogg" onChange={handleAudioChange} />
                <span className="drop-icon"><FileAudio size={21} /></span>
                <span><strong>{audioFile?.name || "Audiobook"}</strong><small>{audioFile ? `${formatBytes(audioFile.size)}${pendingAudioDuration ? ` · ${formatDuration(pendingAudioDuration)}` : ""}` : "MP3, M4A, WAV, FLAC ou OGG"}</small></span>
                <UploadCloud size={18} />
              </label>
            </div>
            <label className="text-editor"><span><strong>Texto que será narrado</strong><small>Remova títulos, notas ou trechos que não aparecem no áudio.</small></span><textarea value={sourceText} onChange={(event) => setSourceText(event.target.value)} placeholder="O texto deste capítulo aparecerá aqui." lang="ja" /><em>{sourceText.length.toLocaleString("pt-BR")} / 675.000</em></label>
            <div className="provider-managed-note"><Cloud size={18} /><span><strong>Processamento protegido</strong><small>O processamento acontece somente no servidor. Nenhuma credencial é solicitada ou enviada ao navegador.</small></span></div>
            {pendingAudioDuration > 0 && <div className="cost-estimate"><Clock3 size={16} /><span><strong>{formatDuration(pendingAudioDuration)} de processamento</strong><small>Estimativa: ~{formatUsd((pendingAudioDuration / 3600) * SPEECH_TO_TEXT_RATE)} na tarifa de US$ 0,22/h. Pode estar incluído no seu plano.</small></span></div>}
            {formError && <div className="form-error" role="alert"><span>{formError}</span></div>}
            <div className="dialog-footer"><p><Sparkles size={15} /> O resultado ficará salvo neste capítulo.</p><button className="align-button" onClick={() => void handleAlignment()} disabled={isProcessing || isParsing || !sourceText.trim() || !audioFile}>{isProcessing ? <LoaderCircle className="spin" size={18} /> : <Sparkles size={18} />}{isProcessing ? "Alinhando com a narração…" : "Criar leitura sincronizada"}</button></div>
          </section>
        </div>
      )}
    </main>
  );
}

function plainBook(book: LibraryBook): StoredBook {
  return {
    id: book.id,
    title: book.title,
    author: book.author,
    sourceName: book.sourceName,
    sourceBlob: book.sourceBlob ?? null,
    sourceKey: book.sourceKey,
    language: book.language,
    currentChapterId: book.currentChapterId,
    createdAt: book.createdAt,
    updatedAt: book.updatedAt,
  };
}

function findCharacterAtTime(characters: AlignedCharacter[], time: number): number {
  if (!characters.length) return -1;
  let low = 0;
  let high = characters.length - 1;
  let best = 0;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    if (characters[middle].start <= time) {
      best = middle;
      low = middle + 1;
    } else high = middle - 1;
  }
  return best;
}

function findSentenceForCharacter(sentences: ReaderSentence[], characterIndex: number): number {
  if (characterIndex < 0 || !sentences.length) return -1;
  let previousSentenceIndex = 0;
  for (let index = 0; index < sentences.length; index += 1) {
    const sentence = sentences[index];
    if (characterIndex < sentence.globalStart) return previousSentenceIndex;
    if (characterIndex < sentence.globalEnd) return index;
    previousSentenceIndex = index;
  }
  return sentences.length - 1;
}

function statusLabel(chapter: StoredChapter): string {
  if (chapter.status === "aligned") return "Sincronizado";
  if (chapter.status === "processing") return "Processando";
  if (chapter.status === "error") return "Precisa de atenção";
  if (chapter.audioBlob && chapter.text) return "Pronto para alinhar";
  return chapter.text ? "Sem áudio" : "Rascunho";
}

function statusIcon(chapter: StoredChapter) {
  if (chapter.status === "aligned") return <Check size={12} />;
  if (chapter.status === "processing") return <LoaderCircle className="spin" size={12} />;
  if (chapter.status === "error") return <CircleAlert size={12} />;
  return <Clock3 size={12} />;
}

function coverGlyph(title: string): string {
  return Array.from(title.trim()).find((character) => /[\p{L}\p{N}]/u.test(character))?.toUpperCase() || "読";
}

function chapterTitleFromText(text: string): string {
  const firstLine = text.split(/\n+/).map((line) => line.trim()).find(Boolean);
  return firstLine && firstLine.length <= 80 ? firstLine : "Capítulo importado";
}

async function readAudioDuration(file: File): Promise<number> {
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<number>((resolve, reject) => {
      const audio = document.createElement("audio");
      audio.preload = "metadata";
      audio.onloadedmetadata = () => resolve(Number.isFinite(audio.duration) ? audio.duration : 0);
      audio.onerror = () => reject(new Error("Não foi possível medir este áudio."));
      audio.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function formatTime(value: number): string {
  if (!Number.isFinite(value) || value < 0) return "00:00";
  const seconds = Math.floor(value % 60);
  const minutes = Math.floor(value / 60) % 60;
  const hours = Math.floor(value / 3600);
  return hours > 0
    ? `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    : `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function formatDuration(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0 min";
  const minutes = Math.round(value / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `${hours}h ${remainder}min` : `${hours}h`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatUsd(value: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(value);
}

function escapeFlashcardHtml(value: string) {
  return value.replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  })[character] || character);
}

function slugTag(value: string) {
  return value.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9\p{L}]+/gu, "-").replace(/^-|-$/g, "").slice(0, 60) || "sem-titulo";
}
